import { Injectable, signal } from '@angular/core';
import { PolicyHolder } from '../../model/policy-holder/policy-holder.model';
import { PersonalInfo } from '../../model/policy-holder/personal-info.model';
import { ContactInfo } from '../../model/policy-holder/contact-info.model';
import { EmploymentInfo } from '../../model/policy-holder/employment-info.model';
import { SelectOption } from '../../../../shared/ui/drop-down/drop-down';
import { Dependent } from '../../model/dependent.model';

@Injectable({
  providedIn: 'root',
})
export class PolicyHolderStateService {
  readonly formModel = signal<PolicyHolder>(this.createInitialPolicyHolder());

  // --------- RESET ---------
  reset(): void {
    this.formModel.set(this.createInitialPolicyHolder());
  }

  // ---------------- DEPENDENT METHODS ----------------

  addDependent(): void {
    this.formModel.update((model) => ({
      ...model,
      dependents: [...model.dependents, this.createInitialDependent()],
    }));
  }

  removeDependent(index: number): void {
    this.formModel.update((model) => ({
      ...model,
      dependents: model.dependents.filter((_, i) => i !== index),
    }));
  }

  resetDependents(): void {
    this.formModel.update((model) => ({
      ...model,
      dependents: [],
    }));

    this.addDependent();
  }

  // ---------------- TEST DATA ----------------

  populateTestData(): void {
    this.formModel.set({
      personalInfo: {
        firstName: 'Juan',
        middleName: 'Santos',
        lastName: 'Dela Cruz',
        suffix: '',
        birthDate: { month: '01', day: '15', year: '1990' },
        gender: 'MALE',
        civilStatus: 'SINGLE',
        nationality: 'FILIPINO',
      },
      contactInfo: {
        address: {
          region: 'REGION I',
          province: 'CAGAYAN VALLEY',
          city: 'MANILA',
          barangay: 'BRGY. 1',
          zipCode: '1000',
          street: '123 Sample St.',
        },
        email: 'test.user@example.com',
        mobile: '09171234567',
        passportNo: 'P1234567',
        passportExpiryDate: '2030-12-31',
      },
      employmentInfo: {
        jobTitle: 'Welder',
        employerName: 'Sample Employer Inc.',
        fullAddress: 'United Arab Emirates',
      },
      dependents: [
        {
          firstName: 'Maria',
          middleName: 'A',
          lastName: 'Dela Cruz',
          suffix: '',
          birthDate: { month: '05', day: '10', year: '2015' },
          relationship: 'CHILD',
        },
      ],
    });
  }

  // --------- DEFAULT FACTORIES ---------

  public createInitialPolicyHolder(): PolicyHolder {
    return {
      personalInfo: this.createInitialPersonalInfo(),
      contactInfo: this.createInitialContactInfo(),
      employmentInfo: this.createInitialEmploymentInfo(),
      dependents: [this.createInitialDependent()],
    };
  }

  private createInitialPersonalInfo(): PersonalInfo {
    return {
      firstName: '',
      middleName: '',
      lastName: '',
      suffix: '',
      birthDate: { month: '', day: '', year: '' },
      gender: 'MALE',
      civilStatus: 'SINGLE',
      nationality: '',
    };
  }

  private createInitialContactInfo(): ContactInfo {
    return {
      address: {
        region: '',
        province: '',
        city: '',
        barangay: '',
        zipCode: '',
        street: '',
      },
      email: '',
      mobile: '',
      passportNo: '',
      passportExpiryDate: '',
    };
  }

  private createInitialEmploymentInfo(): EmploymentInfo {
    return {
      jobTitle: '',
      employerName: '',
      fullAddress: '',
    };
  }

  private createInitialDependent(): Dependent {
    return {
      firstName: '',
      middleName: '',
      lastName: '',
      suffix: '',
      birthDate: { month: '', day: '', year: '' },
      relationship: '',
    };
  }

  readonly CIVIL_STATUS_OPTIONS: SelectOption[] = [
    { description: 'Single', value: 'SINGLE' },
    { description: 'Married', value: 'MARRIED' },
    { description: 'Separated', value: 'SEPARATED' },
    { description: 'Widowed', value: 'WIDOWED' },
  ];

  readonly NATIONALITY_OPTIONS: SelectOption[] = [{ description: 'Filipino', value: 'FILIPINO' }];

  readonly MONTH_OPTIONS: SelectOption[] = Array.from({ length: 12 }, (_, i) => {
    const date = new Date(2000, i, 1);
    return {
      description: date.toLocaleString('en-US', { month: 'long' }),
      value: String(i + 1).padStart(2, '0'),
    };
  });

  readonly DAY_OPTIONS: SelectOption[] = Array.from({ length: 31 }, (_, i) => {
    const day = String(i + 1).padStart(2, '0');
    return { description: day, value: day };
  });

  readonly ADULT_YEAR_OPTIONS: SelectOption[] = (() => {
    const currentYear = new Date().getFullYear();

    // Max age is 64, but we add another year because we derive years from the current date.
    const minAge = 18;
    const maxAge = 65;

    const maxYear = currentYear - minAge;
    const minYear = currentYear - maxAge;

    return Array.from({ length: maxYear - minYear + 1 }, (_, i) => {
      const year = String(maxYear - i);
      return { description: year, value: year };
    });
  })();

}
